import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import {
    toNullIfEmpty,
    toNumberOrNull,
    toFloatOrNull,
    formatTimeSpan,
    toBooleanOrNull, formatShortDate
} from '../../utils/helpers';
import './QueryForm.scss';
import LoadingSpinner from "../Loading-Component/Loading";
import ReactStars from "react-stars";
import { isMobileOnly } from 'react-device-detect';
import { authenticatedFetch } from '../../utils/api';

// updateMode: -1 = read only, 0 = update existing query, 1 = add new query
function QueryForm({
    initialReadOnlyData = {},
    userData = {},
    onSubmit,
    updateMode = 1,
    editData = {}
}) {
    const [queryCategories, setQueryCategories] = useState([]);
    const [queryTypes, setQueryTypes] = useState([]);
    const [resolutionTypes, setResolutionTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [conditionalError, setConditionalError] = useState('');
    const [error, setError] = useState(null);
    const isMobile = isMobileOnly;
    const isReadOnly = updateMode === -1;

    const [formData, setFormData] = useState({
        QueryID: editData.QueryID || null,
        UpdateAdd: editData.UpdateAdd || 1,
        LineID: initialReadOnlyData.lineID || '',
        QueryCategory: editData.QueryCategory ?? 0,
        QueryTypeID: editData.QueryTypeID || '',
        QueryContent: editData.QueryContent || '',
        RoleName: initialReadOnlyData?.roleName || '',

        supportImageFiles: [],
        RaisedBy: userData?.raisedBy || '',
        JobTitle: userData?.jobTitle || '',
        ContactNo: userData?.contactNo || '',
        RaisedByEmail: userData?.raisedByEmail || '',
        SiteRef: initialReadOnlyData.siteRef || '',
        SiteName: initialReadOnlyData.siteName || '',
        DateOfJob: initialReadOnlyData.dateOfJob || '',
        AttendedFrom: initialReadOnlyData.attendedFrom || '',
        AttendedTo: initialReadOnlyData.attendedTo || '',
        HrsBilled: initialReadOnlyData.hrsBilled || 0,
        RateBilled: initialReadOnlyData.rateBilled || 0,
        NettCharge: initialReadOnlyData.nettCharge || 0,
        ProviderName: initialReadOnlyData.providerName || '',
        Department: initialReadOnlyData.Department || '',
        dateLogged: initialReadOnlyData.dateLogged || '',
        parentQuery: initialReadOnlyData.parentQuery || null,
        staffid: initialReadOnlyData.staffid || null,
        dateResolved: initialReadOnlyData.dateResolved || '',
        resolutionID: initialReadOnlyData.resolutionID || null,
        resolvedHrs: initialReadOnlyData.resolvedHrs || 0,
        resolvedRate: initialReadOnlyData.resolvedRate || 0,
        resolvedNett: initialReadOnlyData.resolvedNett || 0,
        Notes: initialReadOnlyData.notes || '',

        CorrectNett: editData.CorrectNett || '',
        CorrectHours: editData.CorrectHours || '',
        CorrectRate: editData.CorrectRate || '',
        CorrectStartTime: editData.CorrectStartTime || '',
        CorrectEndTime: editData.CorrectEndTime || '',
        ResolutionType: editData.ResolutionType || '',
        ResolutionContent: editData.ResolutionContent || '',
        ResolutionSatisfaction: editData.ResolutionSatisfaction || '',
        ProviderResponse: editData.ProviderResponse || '',
        ProviderEvidenceFiles: [],
    });

    const [validated, setValidated] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                setQueryCategories([
                    { key: 0, label: 'Billing' },
                    { key: 1, label: 'Quality' },
                ]);

                const queryTypesResponse = await authenticatedFetch('/ManagerReport/GetQueryTypes', {
                    method: 'GET',
                });
                const data = await queryTypesResponse.json();

                setQueryTypes(data.queryTypes);

                if (updateMode === 0 || isReadOnly) {
                    const resolutionTypesResponse = await authenticatedFetch('/ManagerReport/GetResolutionTypes', {
                        method: 'GET',
                    });

                    const response = await resolutionTypesResponse.json();

                    setResolutionTypes(response.resolutionTypes);
                }
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError('Failed to load form data.');
                setLoading(false);
            }
        };
        fetchData();
    }, [updateMode, isReadOnly]);

    const handleCategoryChange = (e) => {
        if (isReadOnly) return;
        const newCategory = parseInt(e.target.value, 10);
        setFormData((prev) => ({
            ...prev,
            QueryCategory: newCategory,
            QueryTypeID: '',
            QueryContent: '',
            CorrectNett: '',
            CorrectHours: '',
            CorrectRate: '',
            CorrectStartTime: '',
            CorrectEndTime: '',
        }));
    };

    const handleChange = (e) => {
        if (isReadOnly) return;
        const { name, value, type, checked } = e.target;
        let val = value;
        if (type === 'checkbox') {
            val = checked;
        }
        setFormData((prev) => ({
            ...prev,
            [name]: val,
        }));
    };

    const handleResolutionSatisfactionChange = (newValue) => {
        if (isReadOnly) return;
        setFormData((prev) => ({
            ...prev,
            ResolutionSatisfaction: Number(newValue),
        }));
    };

    const handleFileChange = (e, field) => {
        if (isReadOnly) return;
        const files = Array.from(e.target.files);
        setFormData((prev) => ({
            ...prev,
            [field]: files,
        }));
    };

    const selectedQueryType = queryTypes.find(qt => qt.id === parseInt(formData.QueryTypeID, 10));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isReadOnly) return;

        const form = e.currentTarget;
        setValidated(true);
        setConditionalError('');

        if (selectedQueryType?.id === 2) {
            const {
                CorrectNett,
                CorrectHours,
                CorrectRate,
                CorrectStartTime,
                CorrectEndTime,
            } = formData;

            const isAtLeastOneProvided =
                CorrectNett ||
                CorrectHours ||
                CorrectRate ||
                (CorrectStartTime && CorrectEndTime);

            if (!isAtLeastOneProvided && (form.checkValidity() === false || !isAtLeastOneProvided)) {
                e.stopPropagation();
                setConditionalError(
                    'Please provide at least one of the following: Correct Nett, Correct Hours, Correct Rate, or both Correct Start and End Times.'
                );
                return;
            }
        }

        if (form.checkValidity() === false) {
            e.stopPropagation();
            return;
        }

        try {
            const clickedButton = e.nativeEvent.submitter?.value;
            let uRefForAzureImages = "";
            let providerEvidenceRefs = "";
            setLoading(true);

            try {
                if (formData.supportImageFiles.length > 0) {
                    const formDataImages = new FormData();
                    formData.supportImageFiles.forEach(file => {
                        formDataImages.append('files', file);
                    });
                    const uploadResponse = await authenticatedFetch('/Blob/UploadImages', {
                        method: 'POST',
                        body: formDataImages
                    });
                    if (!uploadResponse.ok) {
                        const errorText = await uploadResponse.text();
                        setLoading(false);
                        throw new Error(`Server error: ${uploadResponse.status} - ${errorText}`);
                    }
                    const responseData = await uploadResponse.json();
                    uRefForAzureImages = responseData.uRefs.join(',');
                }
                if (updateMode === 0 && formData.ProviderEvidenceFiles.length > 0) {
                    const formDataProviderImages = new FormData();
                    formData.ProviderEvidenceFiles.forEach(file => {
                        formDataProviderImages.append('files', file);
                    });

                    const uploadProviderResponse = await authenticatedFetch('/Blob/uploadImages', {
                        method: 'POST',
                        body: formDataProviderImages,
                    });
                    const providerResponseData = await uploadProviderResponse.json();
                    providerEvidenceRefs = providerResponseData.uRefs.join(',');
                }

            } catch (error) {
                setLoading(false);
                console.error("Error uploading images:", error);
            }

            const payload = {
                Email: null,
                QueryID: toNumberOrNull(formData.QueryID),
                UpdateAdd: toBooleanOrNull(formData.UpdateAdd),

                LineID: toNumberOrNull(formData.LineID),
                QueryCategory: toNullIfEmpty(formData.QueryCategory),
                QueryTypeID: toNumberOrNull(formData.QueryTypeID),
                QueryContent: toNullIfEmpty(formData.QueryContent),
                RaisedBy: toNullIfEmpty(formData.RaisedBy),
                JobTitle: toNullIfEmpty(formData.JobTitle),
                ContactNo: toNullIfEmpty(formData.ContactNo),
                RaisedByEmail: toNullIfEmpty(formData.RaisedByEmail),
                uRefForAzureImages: toNullIfEmpty(uRefForAzureImages),
                providerEvidenceRefs: toNullIfEmpty(providerEvidenceRefs),
                CorrectNett: toFloatOrNull(formData.CorrectNett),
                CorrectHours: toFloatOrNull(formData.CorrectHours),
                CorrectRate: toFloatOrNull(formData.CorrectRate),
                CorrectStartTime: toNullIfEmpty(formatTimeSpan(formData.CorrectStartTime)),
                CorrectEndTime: toNullIfEmpty(formatTimeSpan(formData.CorrectEndTime)),
                dateLogged: toNullIfEmpty(formData.dateLogged),
                parentQuery: toNumberOrNull(formData.parentQuery),
                siteref: toNullIfEmpty(formData.SiteRef.toString()),
                sitename: toNullIfEmpty(formData.SiteName),
                dateOfJob: toNullIfEmpty(formData.DateOfJob),
                attendedfrom: toNullIfEmpty(formatTimeSpan(formData.AttendedFrom)),
                attendedto: toNullIfEmpty(formatTimeSpan(formData.AttendedTo)),
                hrsBilled: toFloatOrNull(formData.HrsBilled),
                rateBilled: toFloatOrNull(formData.RateBilled),
                staffid: toNumberOrNull(formData.staffid),
                ProviderName: toNullIfEmpty(formData.ProviderName),
                nettCharge: toFloatOrNull(formData.NettCharge),
                queryCorrectNett: toFloatOrNull(formData.CorrectNett),
                queryCorrectHrs: toFloatOrNull(formData.CorrectHours),
                queryCorrectRate: toFloatOrNull(formData.CorrectRate),
                queryCorrectStart: toNullIfEmpty(formatTimeSpan(formData.CorrectStartTime)),
                queryCorrectFinish: toNullIfEmpty(formatTimeSpan(formData.CorrectEndTime)),
                dateResolved: toNullIfEmpty(formData.dateResolved),
                resolutionID: toNumberOrNull(formData.ResolutionType),
                resolvedHrs: toFloatOrNull(formData.resolvedHrs),
                resolvedRate: toFloatOrNull(formData.resolvedRate),
                resolvedNett: toFloatOrNull(formData.resolvedNett),

                ResolutionType: toNumberOrNull(formData.ResolutionType),
                ResolutionContent: toNullIfEmpty(formData.ResolutionContent),
                ResolutionSatisfaction: toNumberOrNull(formData.ResolutionSatisfaction),

                ProviderResponse: toNullIfEmpty(formData.ProviderResponse),
            };

            switch (clickedButton) {
                case 'addFollowUp':

                    const response = await authenticatedFetch('/ManagerReport/copia__submitQuery', {
                        method: 'POST',
                        body: JSON.stringify(payload),
                    });
                    if (response.status === 200) {
                        setLoading(false);
                        alert('Your query has been submitted successfully.');
                        if (onSubmit) onSubmit(response.data);
                    } else {
                        setLoading(false);
                        throw new Error('Submission failed');
                    }

                    break;
                case 'updateQuery':
                    const updateResponse = await authenticatedFetch('/ManagerReport/copia__updateQuery', {
                        method: 'POST',
                        body: JSON.stringify(payload),
                    });
                    if (updateResponse.status === 200) {
                        setLoading(false);
                        alert('Your query has been updated successfully.');
                        if (onSubmit) onSubmit(updateResponse.data);
                    } else {
                        setLoading(false);
                        throw new Error('Update failed');
                    }

                    break;
                case 'closeQuery':
                    const closeResponse = await authenticatedFetch('/ManagerReport/copia__closeQuery', {
                        method: 'POST',
                        body: JSON.stringify(payload),
                    });
                    if (closeResponse.status === 200) {
                        setLoading(false);
                        alert('Your query has been closed successfully.');
                        if (onSubmit) onSubmit(closeResponse.data);
                    } else {
                        setLoading(false);
                        throw new Error('Close failed');
                    }

                    break;
                default:
                    const submitResponse = await authenticatedFetch('/ManagerReport/copia__submitQuery', {
                        method: 'POST',
                        body: JSON.stringify(payload),
                    });
                    if (submitResponse.status === 200) {
                        setLoading(false);
                        alert('Your query has been submitted successfully.');
                        if (onSubmit) onSubmit(submitResponse.data);
                    } else {
                        setLoading(false);
                        throw new Error('Submission failed');
                    }

                    break;
            }
        } catch (err) {
            console.error(err);
            setLoading(false);
            alert('There was an error with your query.');
        }
    };

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    const filteredQueryTypes = queryTypes.filter(
        (qt) => qt.categoryType === formData.QueryCategory
    );

    const selectedCategoryType = queryCategories.find(qC => qC.key === parseInt(formData.QueryCategory, 10))
    const requiresSupportingEvidence = selectedCategoryType?.label.toLowerCase();

    return (
        <>
            <LoadingSpinner loading={loading} />

            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Row className="align-items-center mb-4">
                    <Col md={12} lg={5}>
                        <h3>{isReadOnly ? 'View Query Details' : updateMode === 1 ? 'Raise a New Query' : 'Update Query'}</h3>
                    </Col>
                    <Col md={12} lg={4}>
                        <div className="small text-white">
                            <p><strong className="text-color">Site Reference:</strong> {formData.SiteRef}</p>
                            <p><strong className="text-color">Site Name:</strong> {formData.SiteName}</p>
                            <p><strong className="text-color">Date of Job:</strong> {formatShortDate(formData.DateOfJob)}</p>
                            <p><strong className="text-color">Provider Name:</strong> {formData.ProviderName}</p>
                            <p><strong className="text-color">QueryID:</strong> {formData.QueryID}</p> 
                        </div>
                    </Col>
                    <Col md={12} lg={3}>
                        <div className="small text-white">
                            <p><strong className="text-color">Start Time:</strong> {formData.AttendedFrom}</p>
                            <p><strong className="text-color">End Time:</strong> {formData.AttendedTo}</p>
                            <p><strong className="text-color">Hours Billed:</strong> {formData.HrsBilled}</p>
                            <p><strong className="text-color">Rate Billed:</strong> &pound;{formData.RateBilled}</p>
                            <p><strong className="text-color">Nett Charge:</strong> &pound;{formData.NettCharge}</p>
                            <p><strong className="text-color">Role Name:</strong> {formData.RoleName}</p>
                            <p><strong className="text-color">LineID:</strong> {formData.LineID}</p>
                        </div>
                    </Col>
                </Row>

                <hr />

                {(updateMode === 0 || isReadOnly) && (
                    <Form.Control
                        type="hidden"
                        name="QueryID"
                        value={formData.QueryID}
                    />
                )}

                {(updateMode === 0 || isReadOnly) && (
                    <Form.Group controlId="formNotesContent">
                        <Form.Label>Submission Note/Reference</Form.Label>
                        <Form.Control
                            type="text"
                            rows={3}
                            name="NotesContent"
                            className="mb-3"
                            value={formData.Notes}
                            onChange={handleChange}
                            disabled={true}
                        />
                    </Form.Group>
                )}

                <h5 className="text-color">Query Details</h5>
                <Row className="mb-3">
                    <Col md={12} lg={6}>
                        <Form.Group controlId="formQueryCategory">
                            <Form.Label>Query Category {!isReadOnly && <span className="text-danger">*</span>}</Form.Label>
                            <Form.Select
                                name="QueryCategory"
                                value={formData.QueryCategory}
                                onChange={handleCategoryChange}
                                required={!isReadOnly}
                                disabled={isReadOnly}
                            >
                                <option value="">-- Select Category --</option>
                                {queryCategories.map((cat) => (
                                    <option key={cat.key} value={cat.key}>
                                        {cat.label}
                                    </option>
                                ))}
                            </Form.Select>
                            {!isReadOnly && (
                                <Form.Control.Feedback type="invalid">
                                    Please select a query category.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={12} lg={6}>
                        <Form.Group controlId="formQueryTypeID">
                            <Form.Label>Query Type {!isReadOnly && <span className="text-danger">*</span>}</Form.Label>
                            <Form.Select
                                name="QueryTypeID"
                                value={formData.QueryTypeID}
                                onChange={handleChange}
                                required={!isReadOnly}
                                disabled={isReadOnly}
                            >
                                <option value="">-- Select a Type --</option>
                                {filteredQueryTypes.map((type) => (
                                    <option key={type.id} value={type.id}>
                                        {type.queryType}
                                    </option>
                                ))}
                            </Form.Select>
                            {!isReadOnly && (
                                <Form.Control.Feedback type="invalid">
                                    Please select a query type.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-2" controlId="formQueryContent">
                    <Form.Label>Query Content {!isReadOnly && <span className="text-danger">*</span>}</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        name="QueryContent"
                        value={formData.QueryContent}
                        onChange={handleChange}
                        placeholder={isReadOnly ? "" : "Please provide as much detail as possible to help resolve your issues sooner"}
                        required={!isReadOnly}
                        maxLength={5000}
                        disabled={isReadOnly}
                    />
                    {!isReadOnly && (
                        <>
                            <Form.Control.Feedback type="invalid">
                                Please provide the query content.
                            </Form.Control.Feedback>
                            <Form.Text className="text-white">
                                Maximum 5000 characters.
                            </Form.Text>
                        </>
                    )}
                </Form.Group>

                {requiresSupportingEvidence === "quality" && !isReadOnly && (
                    <Form.Group className="mb-3" controlId="formSupportImage">
                        <Form.Label>Supporting Evidence (Optional)</Form.Label>
                        <Form.Control
                            type="file"
                            multiple
                            onChange={(e) => handleFileChange(e, 'supportImageFiles')}
                            accept="image/*,application/pdf"
                        />
                        <Form.Text className="text-white">
                            You can upload multiple images or documents as supporting evidence.
                        </Form.Text>
                    </Form.Group>
                )}

                <Row className="mb-4">
                    <Col md={12} lg={4} className="raisedby-inputs">
                        <Form.Group controlId="formRaisedBy">
                            <Form.Label>Name {!isReadOnly && <span className="text-danger">*</span>}</Form.Label>
                            <Form.Control
                                type="text"
                                name="RaisedBy"
                                value={formData.RaisedBy}
                                onChange={handleChange}
                                required={!isReadOnly}
                                placeholder={isReadOnly ? "" : "Enter your name"}
                                disabled={isReadOnly}
                            />
                            {!isReadOnly && (
                                <Form.Control.Feedback type="invalid">
                                    Please enter your name.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={12} lg={4} className="raisedby-inputs">
                        <Form.Group controlId="formJobTitle">
                            <Form.Label>Job Title</Form.Label>
                            <Form.Control
                                type="text"
                                name="JobTitle"
                                value={formData.JobTitle}
                                onChange={handleChange}
                                placeholder={isReadOnly ? "" : "Enter your job title"}
                                disabled={isReadOnly}
                            />
                            {!isReadOnly && (
                                <Form.Control.Feedback type="invalid">
                                    Please enter your job title.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={12} lg={4} className="raisedby-inputs">
                        <Form.Group controlId="formContactNo">
                            <Form.Label>Contact Number</Form.Label>
                            <Form.Control
                                type="text"
                                name="ContactNo"
                                value={formData.ContactNo}
                                onChange={handleChange}
                                placeholder={isReadOnly ? "" : "Enter your contact number"}
                                disabled={isReadOnly}
                            />
                            {!isReadOnly && (
                                <Form.Control.Feedback type="invalid">
                                    Please enter your contact number.
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                {formData.QueryCategory === 0 && (
                    <>
                        <h5 className="text-color">Expected Billing (Where known)</h5>
                        <Row className="mb-3 gap-3">
                            <Col md={12} lg={2} className="billing-inputs time-inputs">
                                <Form.Group controlId="formCorrectStartTime">
                                    <Form.Label>Start Time</Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="CorrectStartTime"
                                        value={formData.CorrectStartTime}
                                        onChange={handleChange}
                                        isInvalid={validated && conditionalError}
                                        disabled={isReadOnly}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12} lg={2} className="billing-inputs time-inputs">
                                <Form.Group controlId="formCorrectEndTime">
                                    <Form.Label>End Time</Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="CorrectEndTime"
                                        value={formData.CorrectEndTime}
                                        onChange={handleChange}
                                        isInvalid={validated && conditionalError}
                                        disabled={isReadOnly}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12} lg={2} className="billing-inputs">
                                <Form.Group controlId="formCorrectHours">
                                    <Form.Label>Correct Hours</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="CorrectHours"
                                        value={formData.CorrectHours}
                                        onChange={handleChange}
                                        placeholder={isReadOnly ? "" : "00:00"}
                                        isInvalid={validated && conditionalError}
                                        disabled={isReadOnly}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12} lg={2} className="billing-inputs">
                                <Form.Group controlId="formCorrectRate">
                                    <Form.Label>Correct Rate</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="CorrectRate"
                                        value={formData.CorrectRate}
                                        onChange={handleChange}
                                        placeholder={isReadOnly ? "" : "00.00"}
                                        isInvalid={validated && conditionalError}
                                        disabled={isReadOnly}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12} lg={2} className="mb-3 billing-inputs">
                                <Form.Group controlId="formCorrectNett">
                                    <Form.Label>Correct Nett</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="CorrectNett"
                                        value={formData.CorrectNett}
                                        onChange={handleChange}
                                        placeholder={isReadOnly ? "" : "00.00"}
                                        isInvalid={validated && conditionalError}
                                        disabled={isReadOnly}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}

                {selectedQueryType?.id === 2 && conditionalError && !isReadOnly && (
                    <Alert variant="danger" className="mt-3">
                        {conditionalError}
                    </Alert>
                )}

                {(updateMode === 0 || isReadOnly) && (
                    <>
                        <h5 className="text-color">Provider Response</h5>
                        <Form.Group className="mb-3" controlId="formProviderResponse">
                            <Form.Label>Provider Response</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="ProviderResponse"
                                value={formData.ProviderResponse}
                                onChange={handleChange}
                                placeholder={isReadOnly ? "" : "Optional: Enter provider's response here."}
                                disabled={isReadOnly}
                            />
                        </Form.Group>
                        {!isReadOnly && (
                            <Form.Group className="mb-3" controlId="formProviderEvidence">
                                <Form.Label>Provider Evidence (Optional)</Form.Label>
                                <Form.Control
                                    type="file"
                                    multiple
                                    onChange={(e) => handleFileChange(e, 'ProviderEvidenceFiles')}
                                    accept="image/*,application/pdf"
                                />
                                <Form.Text className="text-white">
                                    You can upload multiple images or documents as provider evidence.
                                </Form.Text>
                            </Form.Group>
                        )}
                    </>
                )}

                {(updateMode === 0 || isReadOnly) && (
                    <>
                        <h5 className="text-color">Resolution Details</h5>
                        <Row className="mb-3">
                            <Col md={12} lg={6}>
                                <Form.Group controlId="formResolutionType">
                                    <Form.Label>Resolution Type</Form.Label>
                                    <Form.Select
                                        name="ResolutionType"
                                        value={formData.ResolutionType}
                                        onChange={handleChange}
                                        disabled={isReadOnly}
                                    >
                                        <option value="">-- Select Resolution Type --</option>
                                        {resolutionTypes.map((res, index) => (
                                            <option key={res.id || index} value={res.id}>
                                                {res.resolution}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {!isReadOnly && (
                                        <Form.Control.Feedback type="invalid">
                                            Please select a resolution type.
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={12} lg={6}>
                                <Form.Group controlId="formResolutionSatisfaction">
                                    <Form.Label>Resolution Satisfaction (0-5 Stars)</Form.Label>
                                    <ReactStars
                                        count={5}
                                        name="ResolutionSatisfaction"
                                        onChange={handleResolutionSatisfactionChange}
                                        value={Number(formData.ResolutionSatisfaction) || 0}
                                        size={25}
                                        half={false}
                                        color2="#e23193"
                                        color1="#999"
                                        edit={!isReadOnly}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group controlId="formResolutionContent">
                            <Form.Label>Resolution Content</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="ResolutionContent"
                                className="mb-3" 
                                value={formData.ResolutionContent}
                                onChange={handleChange}
                                placeholder={isReadOnly ? "" : "Optional: Provide details about the resolution."}
                                disabled={isReadOnly}
                            />
                        </Form.Group>                      
                    </>
                )}

                {updateMode === 0 && !isReadOnly && (
                    <Form.Group className="mb-3" controlId="formUpdateAdd">
                        <h5 className="text-color">Action</h5>
                        <div>
                            <Row className="justify-content-between">
                                <Col md={12} lg={4}>
                                    <Button variant="primary" type="submit" className={`mt-3 mb-3 queryForm-Action-Button`} value="addFollowUp">
                                        Close & Add As Follow-up
                                    </Button>
                                </Col>
                                <Col md={12} lg={3} className="px-2">
                                    <Button variant="primary" type="submit" className={`mt-3 mb-3 queryForm-Action-Button ${isMobile ? "" : "w-100"}`} value="updateQuery">
                                        Save Query
                                    </Button>
                                </Col>
                                <Col md={12} lg={4}>
                                    <Button variant="primary" type="submit" className="mt-3 mb-3 queryForm-Action-Button" value="closeQuery">
                                        Close Down Query
                                    </Button>
                                </Col>
                            </Row>                         
                        </div>
                    </Form.Group>
                )}

                {!isReadOnly && (
                    <div className="mt-3 d-flex justify-content-center pb-3">
                        <div>
                            <small className="text-white">
                                By submitting this query, you acknowledge it will be automatically sent to the provider.
                            </small>
                        </div>
                    </div>
                )}

                {updateMode === 1 && !isReadOnly && (
                    <div className="mt-2 d-flex justify-content-center">
                        <div>
                            <Button variant="primary" type="submit" className="mt-3 mb-3">
                                Submit Query
                            </Button>
                        </div>
                    </div>
                )}

            </Form>
        </>
    );
}

export default QueryForm;